<script lang="ts">
  import { Column, Columns } from '@athena-lib/Columns';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import { TextField } from '@athena-lib/Fields';
  import { PasswordMeter } from '@athena-lib/PasswordMeter';
  import { Stack, StackItem } from '@athena-lib/Stack';
  import { Type } from '@sinclair/typebox';
  import { Button } from '@athena-lib/Button';
  import Spinner from '@athena-lib/Icons/Spinner.svelte';
  import CloseSmall from '@athena-lib/Icons/CloseSmall.svelte';
  import { initPasswordFormTracker, type PasswordData } from './EditPasswordFormTrackingHelper';
  import { post } from '$lib/http';

  const translations = getCommonTranslations();

  let currentPassword = $state('');

  let passwordData: PasswordData = $state({
    newPassword: '',
    confirmPassword: '',
    passwordValid: false,
  });

  let changePasswordState = $state<'initial' | 'loading' | 'successful' | 'error'>('initial');

  const formTracker = initPasswordFormTracker();

  const onSubmit = () => {
    formTracker.sendTrackingEvent('submit');

    changePasswordState = 'loading';
    post(
      '/account/update_password',
      {
        currentPassword,
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword,
      },
      Type.Object({ success: Type.Literal(true) }),
    )
      .then(() => {
        changePasswordState = 'successful';
      })
      .catch((error: Error) => {
        changePasswordState = 'error';
        formTracker.sendTrackingEvent('error', error.message);
      });
  };
</script>

<Columns collapseBelow="md">
  <Column>
    <Stack>
      {#if changePasswordState === 'error'}
        <StackItem class="flex flex-row justify-between rounded bg-st-red p-2 text-st-white">
          {translations.anErrorHasOccured}
          <Button
            size="small"
            color="white"
            class="border-0 !p-0"
            onClick={() => {
              changePasswordState = 'initial';
            }}
          >
            <CloseSmall sizeClass="h-6 w-6" />
          </Button>
        </StackItem>
      {/if}

      {#if changePasswordState === 'successful'}
        <StackItem class="flex flex-row justify-between rounded bg-st-green p-2 text-st-white">
          {translations.passwordSuccessfullyChanged}
        </StackItem>
      {:else}
        <StackItem>
          <TextField
            type="password"
            bind:value={currentPassword}
            label={translations.currentPassword}
            oninput={() => {
              formTracker.onFormInputEvent(passwordData);
            }}
          />
        </StackItem>
        <StackItem>
          <TextField
            type="password"
            bind:value={passwordData.newPassword}
            label={translations.newPassword}
            oninput={() => {
              formTracker.onFormInputEvent(passwordData);
            }}
          />
        </StackItem>
        <StackItem>
          <TextField
            type="password"
            bind:value={passwordData.confirmPassword}
            label={translations.confirmPassword}
            oninput={() => {
              formTracker.onFormInputEvent(passwordData);
            }}
          />
        </StackItem>
        <StackItem class="flex flex-row justify-end">
          <Button onClick={onSubmit} disabled={changePasswordState === 'loading'}>
            {#if changePasswordState === 'loading'}
              <Spinner sizeClass="w-6 h-6" />
            {:else}
              {translations.save}
            {/if}
          </Button>
        </StackItem>
      {/if}
    </Stack>
  </Column>

  {#if changePasswordState !== 'successful'}
    <Column>
      <PasswordMeter input={passwordData.newPassword} bind:isValid={passwordData.passwordValid}
      ></PasswordMeter>
    </Column>
  {/if}
</Columns>
